import { resolveTaxFromCatalog } from '../../drawers/tax-catalog-lookup.util';

/** Un impuesto de línea ya resuelto: lo justo para pintarlo y para sumarlo al Total. */
export interface ResolvedLineTax {
  id: number;
  name: string;
  /** Porcentaje. El maestro admite `amountType`, pero todo el parque real es `Percent`. */
  rate: number;
}

/**
 * CEC-663. De dónde saca la rejilla el nombre y el porcentaje del impuesto de una línea.
 *
 * **Manda el detalle que trae la propia línea; si no viene, se cae al catálogo de la compañía.**
 *
 * El defecto que arregla: la pantalla resolvía el impuesto **sólo** contra el catálogo global, que
 * está filtrado por la compañía **seleccionada en el conmutador**, no por la dueña de la cotización.
 * Medido en DEV el 10/09/2026: con la compañía 3 seleccionada y la cotización S814450 (compañía 4),
 * el catálogo a mano ofrecía `21% S / 10% S / 4% S / 0% S` y la línea llevaba el 143 («19% S»), que
 * no estaba. `find(...)` devolvía `undefined` y de ahí salían **los dos síntomas a la vez**: la
 * columna Impuestos mostraba `–` y el Total quedaba igual al Importe base (67.985,00 US$).
 *
 * Por eso la fuente preferente es la línea: el servidor ya sabe qué impuesto tiene, y lo devuelve
 * con nombre y porcentaje. El catálogo se queda como respaldo —cubre a un backend que todavía no
 * mande el detalle y el caso de una línea recién tocada en la rejilla, donde sólo hay ids.
 *
 * No decide **cómo** se guarda el impuesto —eso es CEC-658, de Leonardo— ni filtra qué impuestos
 * ofrece el desplegable, que es `getCompanyTaxesForProduct`.
 */
export function resolveLineTaxes(
  rawLine: any,
  companyTaxes: readonly any[] | null | undefined,
): ResolvedLineTax[] {
  return readTaxEntries(rawLine)
    .map((entry) => resolveEntry(entry, companyTaxes))
    .filter((tax): tax is ResolvedLineTax => tax !== null);
}

/**
 * El texto de la columna «Impuestos». Conserva el formato que la rejilla ya pintaba
 * —«nombre (porcentaje%)», y el guion cuando no hay ninguno— para no mover nada más que el hueco.
 */
export function formatLineTaxes(taxes: readonly ResolvedLineTax[] | null | undefined): string {
  if (!taxes || taxes.length === 0) {
    return '-';
  }

  return taxes.map((tax) => `${tax.name} (${tax.rate}%)`).join(', ');
}

/**
 * Lo que esta línea aporta al bloque de impuestos del Total. Se calcula sobre el importe que la
 * rejilla ya muestra —el convertido cuando la línea va en otra divisa— y no sobre el `taxAmount`
 * del servidor, que está en la divisa de origen.
 */
export function sumLineTaxAmount(
  lineAmount: number | null | undefined,
  taxes: readonly ResolvedLineTax[] | null | undefined,
): number {
  if (!taxes || taxes.length === 0) {
    return 0;
  }

  const amount = Number(lineAmount) || 0;

  return taxes.reduce((sum, tax) => sum + amount * tax.rate / 100, 0);
}

/**
 * Las formas en que una línea trae sus impuestos, normalizadas a una sola.
 *
 * **`taxIds` decide QUÉ impuestos tiene la línea; las relaciones aportan el DETALLE de cada uno.**
 * Los dos conviven en el mismo objeto tras un guardado: `mergeLineResponseWithPayload` deja los
 * `taxIds` del payload junto a las relaciones que devuelve el servidor. Si los ids ganaran del todo
 * se perdería el detalle y el defecto volvería al editar una línea; si ganaran las relaciones se
 * resucitaría un impuesto que el usuario acaba de quitar. Por eso la lista sale de `taxIds` cuando
 * viene, y el detalle se busca por id entre las relaciones.
 */
function readTaxEntries(rawLine: any): Array<{ id: number; detail?: any }> {
  const relations: any[] = Array.isArray(rawLine?.taxes) ? rawLine.taxes : [];
  const detailsById = new Map<number, any>();

  for (const relation of relations) {
    if (typeof relation === 'object' && relation !== null && relation.tax) {
      detailsById.set(Number(relation.taxId ?? relation.tax.id), relation.tax);
    }
  }

  const raw: unknown[] = Array.isArray(rawLine?.taxIds) ? rawLine.taxIds : relations;

  return raw
    .map((entry: any) => (typeof entry === 'object' && entry !== null
      ? Number(entry.taxId ?? entry.tax?.id ?? entry.id)
      : Number(entry)))
    .filter((id) => Number.isFinite(id) && id > 0)
    .map((id) => ({ id, detail: detailsById.get(id) }));
}

function resolveEntry(
  entry: { id: number; detail?: any },
  companyTaxes: readonly any[] | null | undefined,
): ResolvedLineTax | null {
  const source = entry.detail ?? resolveTaxFromCatalog(companyTaxes as any, entry.id);

  // Un id que no se resuelve en ninguna de las dos fuentes no se puede pintar ni sumar: se
  // descarta en vez de colarse a medias y ensuciar la columna o el Total con un impuesto sin nombre.
  if (!source) {
    return null;
  }

  return {
    id: entry.id,
    name: String(source.name ?? ''),
    rate: unwrapAmount(source.amount),
  };
}

/** El catálogo global entrega `amount` envuelto (`{ parsedValue }`) en algunas rutas del store. */
function unwrapAmount(amount: any): number {
  const value = typeof amount === 'object' && amount !== null && amount.parsedValue !== undefined
    ? amount.parsedValue
    : amount;

  return Number(value) || 0;
}
